import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { api, apiError } from "@/lib/api";

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const forced = !!user?.must_change_password;

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  if (authLoading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (pw.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (pw === current) { setError("New password must be different from your current password."); return; }
    if (pw !== pw2) { setError("Passwords don\u2019t match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pw });
      setDone(true);
      await refresh();
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  function cancel() {
    if (forced) {
      logout();
      navigate("/login");
    } else {
      navigate("/dashboard");
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10 fade-up">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Change Your Password</h1>
        {forced && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="font-semibold">First-time sign in</p>
            <p className="mt-1 text-amber-800">For your security, please choose a new password before continuing to the portal.</p>
          </div>
        )}
        {done ? (
          <div className="mt-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Password updated</h2>
            <p className="mt-1 text-sm text-slate-600">Redirecting to the dashboard\u2026</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {error && <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</p>}
            <div className="space-y-2">
              <Label htmlFor="current" className="text-sm font-semibold text-slate-900">Current Password</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input id="current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Current Password" className="h-11 pl-9" required autoComplete="current-password" />
              </div>
              {forced && <p className="text-xs text-slate-500">Use the temporary password that was emailed to you.</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw" className="text-sm font-semibold text-slate-900">New Password</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New Password" className="h-11 pl-9" required minLength={8} autoComplete="new-password" />
              </div>
              <p className="text-xs text-slate-500">At least 8 characters.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw2" className="text-sm font-semibold text-slate-900">Confirm New Password</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm New Password" className="h-11 pl-9" required minLength={8} autoComplete="new-password" />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button type="submit" className="h-11 flex-1 bg-blue-600 hover:bg-blue-700 sm:flex-none sm:px-8" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Change Password
              </Button>
              <Button type="button" variant="outline" className="h-11" onClick={cancel}>Cancel</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
